import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getReferralUserSettings,
  updateReferralUserSettings,
  getReferralLink,
} from '@cobu/api-client';
import { useSelector } from 'react-redux';
import { Strings } from '../constants/Strings';
import { URLS } from '../constants/Urls';
import { showErrorMessage, showSuccessMessage } from '../utils/messageToast';

// Constants
const REFERRAL_CONFIG_INJECTED_JAVASCRIPT = `(function() {
    window.isPreviewOnly = true;
})();`;

const customizeStrings = Strings.get().referral.customize;

export const useCustomizeSection = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.auth.user);
  const queryClient = useQueryClient();

  const {
    data: userSettings,
    isLoading: settingsLoading,
    error: settingsError,
  } = useQuery({
    queryKey: ['referralUserSettings'],
    queryFn: getReferralUserSettings,
  });

  const { data: referralLink, isLoading: linkLoading } = useQuery({
    queryKey: ['referralLink', user?.building?.uuid || '', user?.uuid || ''],
    queryFn: () => getReferralLink(user?.building?.uuid || '', user?.uuid || ''),
    enabled: !!user?.building?.uuid && !!user?.uuid,
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // Computed values
  const showProfileImage = (userSettings as any)?.details?.includeProfileImage || false;
  const isProfileSet = !!user?.profileImage && user.profileImage !== URLS.DEFAULT_PROFILE_IMAGE;

  // Loading states
  const isLoading = settingsLoading || linkLoading;
  const isActionLoading = isUpdating;

  // Event handlers
  const onToggleProfileImage = useCallback(
    async (value: boolean) => {
      try {
        setIsUpdating(true);
        await updateReferralUserSettings({
          details: {
            includeProfileImage: value,
          },
        });

        // Optimistically update cached settings so UI reflects new state immediately
        queryClient.setQueryData(['referralUserSettings'], (prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            details: {
              ...(prev.details || {}),
              includeProfileImage: value,
            },
          };
        });
        // Optionally refetch in background to ensure consistency
        queryClient.invalidateQueries({ queryKey: ['referralUserSettings'] });

        showSuccessMessage(
          customizeStrings.alertTitles.success,
          value ? customizeStrings.messages.profileAdded : customizeStrings.messages.profileRemoved
        );
      } catch (error) {
        showErrorMessage(customizeStrings.alertTitles.error, customizeStrings.messages.profileUpdateFailed);
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient]
  );

  const onWelcomeMessage = useCallback(() => {
    navigate('/perks/referral/customize-message');
  }, [navigate]);

  const onSetProfileImage = useCallback(() => {
    navigate(`/public-profile/${user?.uuid}`);
  }, [user?.uuid]);

  const previewReferralPage = useCallback(() => {
    if (!referralLink) {
      showErrorMessage(`${customizeStrings.alertTitles.error}: ${customizeStrings.messages.previewFailed}`);
      return;
    }
    // Preview intentionally does not set isAppReferral: this shows the resident
    // the landing page their referred friend will see, in a non-submitting mode.
    const previewUrl = new URL(referralLink, window.location.origin);
    previewUrl.searchParams.set('isPreviewOnly', 'true');

    navigate('/webview', {
      replace: true,
      state: {
        url: previewUrl.toString(),
        title: customizeStrings.previewPageTitle,
        injectedJavaScript: REFERRAL_CONFIG_INJECTED_JAVASCRIPT,
        returnTo: '/perks/referral',
        returnToTab: 'yourResults',
      },
    });
  }, [referralLink, navigate]);

  return {
    // Data
    user,
    userSettings,
    referralLink,

    // Loading states
    isLoading,
    isActionLoading,
    settingsError,

    // Computed values
    showProfileImage,
    isProfileSet,

    // Actions
    onToggleProfileImage,
    onWelcomeMessage,
    onSetProfileImage,
    previewReferralPage,

    // Strings
    strings: customizeStrings,
  };
};
